<script setup lang="ts">
import { Link } from "@element-plus/icons-vue";
import { computed, onMounted, ref } from "vue";
import { LinkedWorkflowSummaryDto } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { parseDate } from "~/utils/dateUtils";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  entityGuid: string;
  /** Whether to include archived workflows */
  includeArchived?: boolean;
  /** Whether to show the interactions section (default true) */
  showInteractions?: boolean;
}>();

const emit = defineEmits<{
  (e: "workflowSelected", workflowGuid: string): void;
}>();

// State
const linkedWorkflows = ref<LinkedWorkflowSummaryDto[]>([]);
const loading = ref(false);
const showArchived = ref(props.includeArchived ?? false);

// Computed: linked interactions only
const linkedInteractions = computed(() => {
  return linkedWorkflows.value.filter(
    (w) =>
      w.workflowDefinitionName === "CRM Interaction Log" ||
      w.workflowDefinitionName === "Interaction Log" ||
      w.workflowDefinitionName === "Legacy Interaction" ||
      w.workflowDefinitionName === "Contact Activity",
  );
});

// Computed: other workflows (non-interactions)
const otherWorkflows = computed(() => {
  return linkedWorkflows.value.filter(
    (w) =>
      w.workflowDefinitionName !== "CRM Interaction Log" &&
      w.workflowDefinitionName !== "Interaction Log" &&
      w.workflowDefinitionName !== "Legacy Interaction" &&
      w.workflowDefinitionName !== "Contact Activity",
  );
});

// Computed: filtered workflows based on archive toggle
const _displayedWorkflows = computed(() => {
  if (showArchived.value) {
    return linkedWorkflows.value;
  }
  return linkedWorkflows.value.filter((w) => !w.workflowArchivedAt);
});

// Computed: filtered interactions based on archive toggle
const displayedInteractions = computed(() => {
  if (showArchived.value) {
    return linkedInteractions.value;
  }
  return linkedInteractions.value.filter((w) => !w.workflowArchivedAt);
});

// Computed: filtered other workflows based on archive toggle
const displayedOtherWorkflows = computed(() => {
  if (showArchived.value) {
    return otherWorkflows.value;
  }
  return otherWorkflows.value.filter((w) => !w.workflowArchivedAt);
});

// Load linked workflows
const loadLinkedWorkflows = async () => {
  if (!props.entityGuid) return;

  loading.value = true;
  try {
    // Always load with archived to allow toggling without refetch
    linkedWorkflows.value =
      await apiClient.workflowInstance.getLinkedWorkflowsWithArchived(
        props.entityGuid,
        true,
      );
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
};

// Format date for display
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = parseDate(dateString);
  return date ? date.toLocaleDateString() : dateString;
};

// Handle workflow click
const handleWorkflowClick = (workflow: LinkedWorkflowSummaryDto) => {
  emit("workflowSelected", workflow.workflowGuid);
};

// Get status display color
// TODO: types should come from enumDescriptions.ts or similar once we have a better understanding of possible statuses
const getStatusType = (
  status: string,
): "success" | "warning" | "danger" | "info" | "primary" => {
  const statusLower = status.toLowerCase();
  if (
    statusLower.includes("complete") ||
    statusLower.includes("approved") ||
    statusLower.includes("accepted")
  ) {
    return "success";
  }
  if (statusLower.includes("pending") || statusLower.includes("review")) {
    return "warning";
  }
  if (statusLower.includes("reject") || statusLower.includes("cancel")) {
    return "danger";
  }
  return "info";
};

onMounted(() => {
  loadLinkedWorkflows();
});

// Expose refresh method for parent components
defineExpose({
  refresh: loadLinkedWorkflows,
});
</script>

<template>
  <!-- <el-checkbox v-model="showArchived" label="Show Archived" size="small" /> -->
  <div v-if="showInteractions !== false" class="entity-workflows-panel">
    <div class="panel-header">
      <div class="header-left">
        <el-icon><Link /></el-icon>
        <span class="header-title">Activities</span>
        <el-tag v-if="linkedInteractions.length > 0" size="small" type="info">
          {{ displayedInteractions.length }}
          <span
            v-if="
              !showArchived &&
              linkedInteractions.length !== displayedInteractions.length
            "
          >
            / {{ linkedInteractions.length }}
          </span>
        </el-tag>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <el-skeleton :rows="2" animated />
    </div>

    <div v-else-if="displayedInteractions.length === 0" class="empty-state">
      <span>No contact activity</span>
    </div>

    <el-table
      v-else
      :data="displayedInteractions"
      stripe
      style="width: 100%"
      size="small"
      class="workflows-table"
      @row-click="handleWorkflowClick"
    >
      <el-table-column label="Type" width="180">
        <template #default="{ row }">
          {{ row.workflowDefinitionName }}
        </template>
      </el-table-column>

      <el-table-column label="Interaction" width="500">
        <template #default="{ row }">
          <div class="workflow-name-cell">
            <el-link type="primary">{{ row.workflowName }}</el-link>
            <el-tag
              v-if="row.workflowArchivedAt"
              size="small"
              type="info"
              effect="plain"
            >
              Archived
            </el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Status" width="150">
        <template #default="{ row }">
          <el-tag :type="getStatusType(row.workflowStatus)" size="small">
            {{ row.workflowStatus }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Primary Entity" width="140">
        <template #default="{ row }">
          <span v-if="row.primaryEntityName" class="primary-entity">
            {{ row.primaryEntityName }}
          </span>
          <span v-else class="no-entity">-</span>
        </template>
      </el-table-column>

      <el-table-column label="Relationship" width="120">
        <template #default="{ row }">
          <el-tag
            v-if="row.linkType === 'Primary'"
            size="small"
            type="success"
            effect="light"
          >
            Primary
          </el-tag>
          <el-tag
            v-else-if="row.linkType"
            size="small"
            type="info"
            effect="plain"
          >
            {{ row.linkType }}
          </el-tag>
          <span v-else class="no-link-type">Linked</span>
        </template>
      </el-table-column>

      <el-table-column label="Linked">
        <template #default="{ row }">
          {{ formatDate(row.linkedAt) }}
        </template>
      </el-table-column>
    </el-table>
  </div>

  <div
    v-if="showInteractions === false || otherWorkflows.length > 0"
    class="entity-workflows-panel"
  >
    <div class="panel-header">
      <div class="header-left">
        <span class="header-title">Forms</span>
        <el-tag v-if="otherWorkflows.length > 0" size="small" type="info">
          {{ displayedOtherWorkflows.length }}
          <span
            v-if="
              !showArchived &&
              otherWorkflows.length !== displayedOtherWorkflows.length
            "
          >
            / {{ otherWorkflows.length }}
          </span>
        </el-tag>
      </div>
    </div>

    <el-table
      v-if="displayedOtherWorkflows.length > 0"
      :data="displayedOtherWorkflows"
      stripe
      style="width: 100%"
      size="small"
      class="workflows-table"
      @row-click="handleWorkflowClick"
    >
      <el-table-column label="Name" width="500">
        <template #default="{ row }">
          <div class="workflow-name-cell">
            <el-link type="primary">{{ row.workflowName }}</el-link>
            <el-tag
              v-if="row.workflowArchivedAt"
              size="small"
              type="info"
              effect="plain"
            >
              Archived
            </el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="Type" width="140">
        <template #default="{ row }">
          {{ row.workflowDefinitionName }}
        </template>
      </el-table-column>

      <el-table-column label="Status" width="150">
        <template #default="{ row }">
          <el-tag :type="getStatusType(row.workflowStatus)" size="small">
            {{ row.workflowStatus }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Primary Entity" width="140">
        <template #default="{ row }">
          <span v-if="row.primaryEntityName" class="primary-entity">
            {{ row.primaryEntityName }}
          </span>
          <span v-else class="no-entity">-</span>
        </template>
      </el-table-column>

      <el-table-column label="Relationship" width="120">
        <template #default="{ row }">
          <el-tag
            v-if="row.linkType === 'Primary'"
            size="small"
            type="success"
            effect="light"
          >
            Primary
          </el-tag>
          <el-tag
            v-else-if="row.linkType"
            size="small"
            type="info"
            effect="plain"
          >
            {{ row.linkType }}
          </el-tag>
          <span v-else class="no-link-type">Linked</span>
        </template>
      </el-table-column>

      <el-table-column label="Linked">
        <template #default="{ row }">
          {{ formatDate(row.linkedAt) }}
        </template>
      </el-table-column>
    </el-table>
    <!-- </div> -->
  </div>
</template>

<style lang="less" scoped>
.entity-workflows-panel {
  background-color: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color-light);
  border-radius: var(--el-border-radius-base);
  padding: 12px 16px;

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;

      .el-icon {
        font-size: 18px;
        color: var(--el-color-primary);
      }

      .header-title {
        font-weight: 600;
        font-size: 14px;
        color: var(--el-text-color-primary);
      }
    }
  }

  .loading-state {
    padding: 8px 0;
  }

  .empty-state {
    color: var(--el-text-color-secondary);
    font-size: 13px;
    padding: 8px 0;
    text-align: center;
  }

  .workflows-table {
    :deep(.el-table__row) {
      cursor: pointer;

      &:hover {
        background-color: var(--el-fill-color);
      }
    }

    .workflow-name-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .primary-entity {
      color: var(--el-text-color-regular);
    }

    .no-entity,
    .no-link-type {
      color: var(--el-text-color-placeholder);
    }
  }
}
</style>
